import { useState } from "react";
import { Link } from "react-router-dom";
import { useCommitState } from "../data/usePage.js";
import { useRepo } from "../data/repo.js";
import { postCommit } from "../api.js";
import { Dialog } from "./Dialog.js";
import { invalidate } from "../data/useFetch.js";
import type {
  SessionInfo,
  CommitEntry,
  WorktreeSyncState,
} from "../../shared/types.js";

const MD = /\.(md|markdown)$/;

function fileToHref(repo: string, p: string): string {
  return `/${repo}/${p.replace(MD, "")}`;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function invalidateGitAndContent(): void {
  invalidate("commit");
  invalidate("session");
  invalidate("tree");
  invalidate("page:");
  // Publishing may have changed query files — re-run embeds instead of serving
  // the stale client query cache.
  invalidate("query:");
}

export function GitControl({ session }: { session: SessionInfo }) {
  if (!session.editing) {
    return (
      <section className="dw-card">
        <div className="dw-muted dw-card-body">sign in to edit</div>
      </section>
    );
  }
  return <GitControlActive />;
}

function GitControlActive() {
  const repo = useRepo();
  const c = useCommitState();
  const [message, setMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Publish outcome shown in a modal (with a link to the repo page) rather than
  // inline: a merge that couldn't push, or that failed outright.
  const [publishResult, setPublishResult] = useState<{
    tone: "warn" | "error";
    title: string;
    message: string;
  } | null>(null);
  // Collapsed by default to a one-line ⎇ branch (N) badge; expand for the full
  // card (files / commits / publish / discard).
  const [expanded, setExpanded] = useState(false);

  if (c.status === "loading")
    return (
      <section className="dw-card">
        <div className="dw-card-body dw-muted">loading…</div>
      </section>
    );
  if (c.status === "error")
    return (
      <section className="dw-card">
        <pre className="dw-error">{c.error.message}</pre>
      </section>
    );

  const state = c.data;

  const run = async (
    action: "commit" | "merge" | "rebase" | "abandon",
  ): Promise<void> => {
    if (action === "abandon") {
      const warn =
        state.ahead > 0
          ? `Discard ${state.ahead} unmerged commit${state.ahead === 1 ? "" : "s"}?`
          : `Discard branch ${state.branch}?`;
      if (!window.confirm(warn)) return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await postCommit(repo, message, action);
      invalidateGitAndContent();
      if (action === "commit") {
        setMessage("");
        setShowMessage(false);
      }
      // Publish landed locally but didn't reach the upstream — surface it.
      if (action === "merge" && r.pushError) {
        setPublishResult({
          tone: "warn",
          title: "Published locally — not pushed",
          message: r.pushError,
        });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // A failed publish gets the modal (with the repo-page link); other
      // actions keep the inline error.
      if (action === "merge") {
        setPublishResult({ tone: "error", title: "Publish failed", message: msg });
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!state.branch) return null;

  // Abbreviated: pending = uncommitted files + unmerged commits.
  const pending = state.changed.length + state.ahead;
  if (!expanded) {
    return (
      <section className="dw-card dw-git-collapsed">
        <button
          type="button"
          className="dw-git-abbrev"
          onClick={() => setExpanded(true)}
          title="Show worktree details"
        >
          ⎇ {state.branch}
          {pending > 0 && <span className="dw-git-abbrev-n"> ({pending})</span>}
        </button>
      </section>
    );
  }

  return (
    <section className="dw-card dw-git-card">
      <header className="dw-git-branch">
        <button
          type="button"
          className="dw-git-branch-toggle"
          onClick={() => setExpanded(false)}
          title="Hide worktree details"
        >
          ⎇ {state.branch}
        </button>
      </header>
      <SyncSection
        behind={state.behind}
        sync={state.sync}
        busy={busy}
        onSync={() => run("rebase")}
      />
      <ModifiedSection
        repo={repo}
        changed={state.changed}
        busy={busy}
        message={message}
        setMessage={setMessage}
        showMessage={showMessage}
        setShowMessage={setShowMessage}
        showPublish={state.commits.length === 0}
        onCommit={() => run("commit")}
        onPublish={() => run("merge")}
      />
      <CommitsSection repo={repo} commits={state.commits} />
      <div className="dw-git-foot">
        {state.commits.length > 0 && (
          <button
            className="primary dw-git-publish"
            disabled={busy || state.ahead === 0}
            onClick={() => run("merge")}
            title="Merge into main and push"
          >
            Publish ({state.ahead})
          </button>
        )}
        <button
          className="dw-git-discard"
          disabled={busy}
          onClick={() => run("abandon")}
          title="Discard this branch"
        >
          discard branch
        </button>
      </div>
      {error && <pre className="dw-error">{error}</pre>}
      {publishResult && (
        <Dialog
          title={publishResult.title}
          tone={publishResult.tone}
          onClose={() => setPublishResult(null)}
        >
          <pre>{publishResult.message}</pre>
          <p>
            See{" "}
            <Link to={`/_repo/${repo}`} onClick={() => setPublishResult(null)}>
              the repo page
            </Link>{" "}
            to push to GitHub or check the connection.
          </p>
        </Dialog>
      )}
    </section>
  );
}

/**
 * Where this draft sits relative to main. Normally invisible: the server
 * auto-rebases editing branches whenever main moves, so `behind` is 0. It
 * appears only when the auto-rebase hit a conflict (with a retry button —
 * resolving means editing the conflicting file or discarding) or in the brief
 * window before a sync lands.
 */
function SyncSection({
  behind,
  sync,
  busy,
  onSync,
}: {
  behind: number;
  sync: WorktreeSyncState | null;
  busy: boolean;
  onSync: () => void;
}) {
  const conflicted = sync?.status === "conflict" && behind > 0;
  if (!conflicted && behind === 0) return null;
  return (
    <div className="dw-git-section dw-git-sync">
      <div className={conflicted ? "dw-git-sync-conflict" : "dw-muted"}>
        {behind} commit{behind === 1 ? "" : "s"} behind main
        {conflicted && " — automatic update hit a conflict"}
      </div>
      <div className="dw-git-actions">
        <button disabled={busy} onClick={onSync} title="Rebase this draft onto main">
          Sync with main
        </button>
      </div>
    </div>
  );
}

interface ModifiedProps {
  repo: string;
  changed: { code: string; path: string }[];
  busy: boolean;
  message: string;
  setMessage: (s: string) => void;
  showMessage: boolean;
  setShowMessage: (b: boolean) => void;
  /** Inline Publish alongside Commit, used when there are no prior commits. */
  showPublish: boolean;
  onCommit: () => void;
  onPublish: () => void;
}

function ModifiedSection({
  repo,
  changed,
  busy,
  message,
  setMessage,
  showMessage,
  setShowMessage,
  showPublish,
  onCommit,
  onPublish,
}: ModifiedProps) {
  if (changed.length === 0) return null;
  return (
    <div className="dw-git-section">
      <ul className="dw-git-files">
        {changed.map((f) => (
          <li key={f.path}>
            <span className="dw-code">{f.code || "?"}</span>
            <Link to={fileToHref(repo, f.path)} className="dw-git-filelink">
              {f.path}
            </Link>
          </li>
        ))}
      </ul>
      {showMessage && (
        <textarea
          className="dw-git-message"
          rows={2}
          placeholder="Commit message (optional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      )}
      <div className="dw-git-actions">
        <button
          className={showPublish ? "" : "primary"}
          disabled={busy}
          onClick={onCommit}
        >
          Commit
        </button>
        {showPublish && (
          <button className="primary" disabled={busy} onClick={onPublish}>
            Publish
          </button>
        )}
      </div>
      <button
        type="button"
        className="dw-git-msg-toggle"
        onClick={() => setShowMessage(!showMessage)}
        aria-pressed={showMessage}
      >
        {showMessage ? "Hide message" : "Add message"}
      </button>
    </div>
  );
}

function CommitsSection({ repo, commits }: { repo: string; commits: CommitEntry[] }) {
  if (commits.length === 0) return null;
  return (
    <div className="dw-git-section">
      <ul className="dw-commits">
        {commits.map((c) => (
          <CommitItem key={c.hash} repo={repo} c={c} />
        ))}
      </ul>
    </div>
  );
}

function CommitItem({ repo, c }: { repo: string; c: CommitEntry }) {
  return (
    <li className="dw-commit">
      <div className="dw-commit-meta">
        <span className="dw-commit-author">{c.author}</span>
        <span className="dw-commit-sep">·</span>
        <span title={c.date}>{timeAgo(c.date)}</span>
      </div>
      {c.message && <div className="dw-commit-msg">{c.message}</div>}
      {c.files.length > 0 && (
        <ul className="dw-commit-files">
          {c.files.map((f) => (
            <li key={f}>
              <Link to={fileToHref(repo, f)}>{f}</Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
